import React, { useEffect, useState } from "react";
import { api, formatApiError, todayStr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineItemsEditor, emptyLine, linesValid } from "@/components/stock/LineItemsEditor";
import { FormField, WarehouseSelect, SegToggle } from "@/components/stock/FormField";
import { POReceiptForm } from "@/components/purchasing/POReceiptForm";
import { toast } from "sonner";

const TITLES = { in: "stock_in", out: "stock_out", used: "used" };
const COLORS = {
  in: "bg-emerald-700 hover:bg-emerald-800",
  out: "bg-red-700 hover:bg-red-800",
  used: "bg-amber-600 hover:bg-amber-700",
};
const blank = (type) => ({ type, date: todayStr(), warehouse: "central", ref_no: "", note: "", lines: [emptyLine()] });

// Barang Masuk (from PO or manual), Barang Keluar and Pemakaian (used).
export const TxnDialog = ({ type, onClose, items, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(blank("in"));
  const [source, setSource] = useState("po");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!type) return;
    setForm(blank(type));
    setSource("po");
  }, [type]);
  const done = () => { onClose(); onSaved(); };

  const save = async () => {
    if (!linesValid(form.lines)) return toast.error(t("lines_invalid"));
    setBusy(true);
    try {
      const lines = form.lines.map((l) => ({ item_type: l.item_type, item_id: l.item_id, qty: Number(l.qty) }));
      const { data } = await api.post("/stock-transactions", { ...form, lines });
      toast.success(`${t("saved")} · ${data.doc_no}`);
      done();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const fromPO = type === "in" && source === "po";
  return (
    <Dialog open={!!type} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="txn-dialog">
        <DialogHeader><DialogTitle>{type && t(TITLES[type])}</DialogTitle></DialogHeader>
        {type === "in" && (
          <SegToggle value={source} onChange={setSource} testid="txn-source"
                     options={[{ v: "po", label: t("from_po") }, { v: "manual", label: t("without_po") }]} />
        )}
        {fromPO ? (
          <POReceiptForm items={items} onDone={done} onCancel={onClose} />
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <FormField label={t("date")}>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="txn-date-input" />
              </FormField>
              <FormField label={t("warehouse")}>
                <WarehouseSelect value={form.warehouse} onChange={(v) => setForm({ ...form, warehouse: v })} availableOnly={type === "in"} testid="txn-warehouse-select" />
              </FormField>
              <FormField label={t("ref_no")}>
                <Input value={form.ref_no} onChange={(e) => setForm({ ...form, ref_no: e.target.value })} data-testid="txn-ref-input" />
              </FormField>
              <FormField label={t("note")}>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="txn-note-input" />
              </FormField>
            </div>
            <LineItemsEditor lines={form.lines} onChange={(lines) => setForm({ ...form, lines })} items={items}
                             showStock warehouse={form.warehouse} testid="txn-lines" />
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
              <Button onClick={save} disabled={busy} className={COLORS[type]} data-testid="txn-save-btn">{t("save")}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
